import React, { useEffect, useRef, useState } from "react";
import { Plus, Check, X, SkipForward, Flag, Timer, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { kgToLb, lbToKg } from "@/lib/units";
import api, { API_BASE } from "@/lib/api";
import ExerciseIcon from "@/components/ExerciseIcon";

const inputCls =
  "w-full bg-obsidian-900 border border-obsidian-600 px-2.5 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";

const STORE_KEY = "fp_live_workout";

const fmtClock = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const fmtWeight = (kg, isImperial) =>
  isImperial ? `${Math.round(kgToLb(kg) * 2) / 2} lbs` : `${kg} kg`;

// Progressive-overload target from the athlete's last performance of an
// exercise (same heuristic as the backend AI fallback): last top set felt
// RPE ≤ 8 → +2.5% weight; harder than that → same weight, +1 rep.
export const suggestTarget = (history, exerciseName) => {
  const name = (exerciseName || "").trim().toLowerCase();
  if (!name || !Array.isArray(history)) return null;
  // Sort newest-first locally so the "most recent performance" guarantee
  // doesn't depend on the caller's ordering.
  const ordered = [...history].sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || ""),
  );
  for (const w of ordered) {
    const ex = (w.exercises || []).find((e) => (e.name || "").trim().toLowerCase() === name);
    if (!ex || !(ex.sets || []).length) continue;
    const top = ex.sets.reduce((a, b) => (Number(b.weight_kg) > Number(a.weight_kg) ? b : a));
    const lastW = Number(top.weight_kg) || 0;
    const lastR = Number(top.reps) || 0;
    const rpe = top.rpe == null ? null : Number(top.rpe);
    if (rpe != null && rpe > 8) {
      return { last: top, weight_kg: lastW, reps: lastR + 1, reason: "same weight, one more rep" };
    }
    const bumped = Math.round(lastW * 1.025 * 2) / 2; // +2.5%, nearest 0.5kg
    return {
      last: top,
      weight_kg: bumped > lastW ? bumped : lastW + 0.5,
      reps: lastR,
      reason: "+2.5% load",
    };
  }
  return null;
};

export const readLiveSession = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "null");
  } catch {
    return null;
  }
};
export const writeLiveSession = (session) =>
  localStorage.setItem(STORE_KEY, JSON.stringify(session));
export const clearLiveSession = () => localStorage.removeItem(STORE_KEY);

// Mirror the draft to the server. Uses raw fetch with keepalive so the
// request survives the tab being closed/hidden (axios can't do keepalive).
const syncDraftToServer = (session) => {
  try {
    const token = localStorage.getItem("fp_token");
    if (!token || !session) return;
    fetch(`${API_BASE}/workouts/draft`, {
      method: "PUT",
      keepalive: true,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ session }),
    }).catch(() => {});
  } catch {}
};

export const deleteServerDraft = () => api.delete("/workouts/draft").catch(() => {});

const notifyRestOver = async (exerciseName) => {
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const reg = await navigator.serviceWorker?.getRegistration();
    reg?.active?.postMessage({
      type: "SHOW",
      title: "Rest over — back to work",
      body: exerciseName ? `Next set: ${exerciseName}` : "Time for your next set.",
      tag: "rest-timer",
      url: "/workouts",
    });
  } catch {}
};

/**
 * LiveWorkout — "workout in progress" mode. Log sets one at a time as you
 * train; each logged set auto-starts the exercise's rest countdown. The
 * session (sets, clock) persists to localStorage so a refresh or crash
 * mid-workout loses nothing. Finish builds a standard POST /workouts payload
 * handed back to the parent (which already handles offline queueing).
 */
export default function LiveWorkout({ initial, isImperial, onFinish, onCancel, history = [], videoForExercise, onPlayVideo }) {
  // initial = { title, exercises: [{name, rest_seconds}] } from the planner
  // form, or a previously persisted session (resume).
  const [session, setSession] = useState(() => {
    const resumed = readLiveSession();
    if (resumed) return resumed;
    return {
      title: initial?.title || `Workout ${new Date().toLocaleDateString()}`,
      started_at: new Date().toISOString(),
      notes: "",
      exercises: (initial?.exercises || [])
        .filter((e) => e.name)
        .map((e) => ({ name: e.name, rest_seconds: Number(e.rest_seconds) || 90, sets: [] })),
    };
  });
  const [activeIdx, setActiveIdx] = useState(0);
  const [draft, setDraft] = useState({ reps: 10, weight: "", rpe: 8 });
  // Rest timer is anchored to a wall-clock end timestamp so throttled/locked
  // mobile tabs can't drift the countdown — remaining is derived each tick.
  const [rest, setRest] = useState(null); // {endsAt, total, remaining}
  const [elapsed, setElapsed] = useState(0);
  const [newExName, setNewExName] = useState("");
  const restRef = useRef(null);

  // Persist every change so nothing is lost mid-session. localStorage is the
  // instant copy; the server mirror (throttled to ~10s) survives tab closes
  // and lets the athlete resume on another device.
  const sessionRef = useRef(session);
  const syncTimerRef = useRef(null);
  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify(session));
    sessionRef.current = session;
    if (!syncTimerRef.current) {
      syncTimerRef.current = setTimeout(() => {
        syncTimerRef.current = null;
        syncDraftToServer(sessionRef.current);
      }, 10000);
    }
  }, [session]);

  // Flush the draft to the server the moment the tab is hidden or closed.
  useEffect(() => {
    const flush = () => syncDraftToServer(sessionRef.current);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
    };
  }, []);

  // Session clock.
  useEffect(() => {
    const tick = () =>
      setElapsed(Math.max(0, Math.floor((Date.now() - new Date(session.started_at)) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [session.started_at]);

  // Rest countdown (wall-clock based).
  useEffect(() => {
    if (!rest) return undefined;
    restRef.current = setInterval(() => {
      setRest((r) => {
        if (!r) return null;
        const remaining = Math.max(0, Math.ceil((r.endsAt - Date.now()) / 1000));
        if (remaining <= 0) {
          clearInterval(restRef.current);
          toast.success("Rest over — next set!");
          notifyRestOver(session.exercises[activeIdx]?.name);
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
          return null;
        }
        return { ...r, remaining };
      });
    }, 500);
    return () => clearInterval(restRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(rest)]);

  const active = session.exercises[activeIdx];
  const target = active ? suggestTarget(history, active.name) : null;

  const applyTarget = () => {
    if (!target) return;
    setDraft((d) => ({
      reps: target.reps,
      weight: isImperial ? Math.round(kgToLb(target.weight_kg) * 2) / 2 : target.weight_kg,
      rpe: d.rpe, // keep the athlete's own RPE draft
    }));
  };

  const logSet = () => {
    if (!active) return;
    const reps = Number(draft.reps);
    if (!reps || reps < 1) return toast.error("Enter reps first");
    const weight_kg = draft.weight === "" ? 0 : isImperial ? lbToKg(Number(draft.weight)) : Number(draft.weight);
    const set = { reps, weight_kg: Math.round(weight_kg * 100) / 100, rpe: draft.rpe ? Number(draft.rpe) : null };
    setSession((s) => ({
      ...s,
      exercises: s.exercises.map((e, i) => (i === activeIdx ? { ...e, sets: [...e.sets, set] } : e)),
    }));
    const total = Number(active.rest_seconds) || 90;
    setRest({ endsAt: Date.now() + total * 1000, total, remaining: total });
  };

  const addExercise = () => {
    const name = newExName.trim();
    if (!name) return;
    setSession((s) => ({
      ...s,
      exercises: [...s.exercises, { name, rest_seconds: 90, sets: [] }],
    }));
    setActiveIdx(session.exercises.length);
    setNewExName("");
  };

  const finish = () => {
    const done = session.exercises.filter((e) => e.sets.length > 0);
    if (!done.length) return toast.error("Log at least one set before finishing");
    const duration_min = Math.max(1, Math.round(elapsed / 60));
    clearLiveSession();
    deleteServerDraft();
    onFinish({
      title: session.title,
      duration_min,
      perceived_exertion: null,
      notes: session.notes || null,
      exercises: done.map((e) => ({
        name: e.name,
        rest_seconds: Number(e.rest_seconds) || null,
        notes: null,
        sets: e.sets,
      })),
    });
  };

  const cancel = () => {
    if (!window.confirm("Discard this live workout? Logged sets will be lost.")) return;
    clearLiveSession();
    deleteServerDraft();
    onCancel();
  };

  const totalSets = session.exercises.reduce((n, e) => n + e.sets.length, 0);

  return (
    <div className="obs-card border-blaze/40" data-testid="live-workout">
      {/* Header: LIVE badge + clock */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest px-2.5 py-1 bg-blaze/15 border border-blaze/50 text-blaze rounded-sm">
          <span className="h-2 w-2 rounded-full bg-blaze animate-pulse" /> Live
        </span>
        <input
          value={session.title}
          onChange={(e) => setSession((s) => ({ ...s, title: e.target.value }))}
          className="flex-1 min-w-[140px] bg-transparent font-heading text-2xl uppercase focus:outline-none border-b border-transparent focus:border-blaze"
          data-testid="live-title"
        />
        <div className="font-data text-2xl tabular-nums" data-testid="live-elapsed">
          {fmtClock(elapsed)}
        </div>
        <button type="button" onClick={cancel} data-testid="live-cancel-btn" className="p-2 text-zinc-500 hover:text-blaze" aria-label="Discard workout">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Rest timer */}
      {rest && (
        <div className="border border-blaze/50 bg-blaze/10 rounded-sm p-4 mb-4 text-center" data-testid="rest-timer">
          <div className="overline text-blaze mb-1 flex items-center justify-center gap-2">
            <Timer className="h-3 w-3" /> Resting
          </div>
          <div className="font-data text-5xl tabular-nums mb-2">{fmtClock(rest.remaining)}</div>
          <div className="h-1 bg-obsidian-600 rounded-full overflow-hidden mb-3">
            <div
              className="h-full bg-blaze transition-all duration-1000"
              style={{ width: `${(rest.remaining / rest.total) * 100}%` }}
            />
          </div>
          <div className="flex justify-center gap-2">
            <button type="button" data-testid="rest-add-30" onClick={() => setRest((r) => r && { ...r, endsAt: r.endsAt + 30000, total: r.total + 30, remaining: r.remaining + 30 })} className="ghost-btn px-3 py-1.5 text-[11px]">
              +30s
            </button>
            <button type="button" data-testid="rest-skip" onClick={() => setRest(null)} className="ghost-btn px-3 py-1.5 text-[11px] inline-flex items-center gap-1.5">
              <SkipForward className="h-3 w-3" /> Skip rest
            </button>
          </div>
        </div>
      )}

      {/* Exercises */}
      <div className="space-y-3 mb-4">
        {session.exercises.map((ex, i) => (
          <div
            key={i}
            className={`border rounded-sm p-3 ${i === activeIdx ? "border-blaze/60 bg-obsidian-900/60" : "border-obsidian-600 bg-obsidian-900/30"}`}
            data-testid={`live-exercise-${i}`}
          >
            <button
              type="button"
              onClick={() => setActiveIdx(i)}
              className="flex items-center justify-between w-full text-left gap-3"
            >
              <span className="flex items-center gap-3 min-w-0">
                <ExerciseIcon key={ex.name} exercise={{ name: ex.name }} size="sm" />
                <span className="font-heading text-lg uppercase inline-flex items-center gap-2 min-w-0">
                  <span className="truncate">{ex.name}</span>
                  {videoForExercise?.(ex.name) && (
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`live-cue-video-${i}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlayVideo?.(videoForExercise(ex.name));
                      }}
                      onKeyDown={(e) => e.key === "Enter" && onPlayVideo?.(videoForExercise(ex.name))}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-blaze/40 text-blaze hover:bg-blaze/10 rounded-sm cursor-pointer shrink-0"
                      title="Watch your coach's cue video"
                    >
                      <PlayCircle className="h-3 w-3" /> Video
                    </span>
                  )}
                </span>
              </span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">
                {ex.sets.length} set{ex.sets.length === 1 ? "" : "s"} · rest {ex.rest_seconds}s
              </span>
            </button>

            {ex.sets.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {ex.sets.map((s, j) => (
                  <span key={j} className="text-[11px] px-2 py-1 border border-obsidian-600 rounded-sm text-zinc-300 bg-obsidian-900" data-testid={`live-set-chip-${i}-${j}`}>
                    {s.reps} × {fmtWeight(s.weight_kg, isImperial)}{s.rpe ? ` @${s.rpe}` : ""}
                  </span>
                ))}
              </div>
            )}

            {i === activeIdx && (
              <>
                {target && (
                  <div
                    className="flex items-center justify-between gap-2 mt-3 px-3 py-2 border border-blaze/30 bg-blaze/5 rounded-sm"
                    data-testid="live-target-hint"
                  >
                    <p className="text-xs text-zinc-300 min-w-0">
                      <span className="text-zinc-500">Last time:</span>{" "}
                      {target.last.reps} × {fmtWeight(target.last.weight_kg, isImperial)}
                      {target.last.rpe ? ` @${target.last.rpe}` : ""}
                      <span className="text-zinc-500"> · Try:</span>{" "}
                      <span className="text-blaze font-medium">
                        {target.reps} × {fmtWeight(target.weight_kg, isImperial)}
                      </span>{" "}
                      <span className="text-zinc-500">({target.reason})</span>
                    </p>
                    <button
                      type="button"
                      data-testid="live-target-apply"
                      onClick={applyTarget}
                      className="text-[10px] uppercase tracking-widest text-blaze hover:underline whitespace-nowrap"
                    >
                      Apply
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-12 gap-2 mt-3 items-end">
                <div className="col-span-3">
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Reps</label>
                  <input data-testid="live-reps" type="number" value={draft.reps} onChange={(e) => setDraft((d) => ({ ...d, reps: e.target.value }))} className={inputCls} />
                </div>
                <div className="col-span-3">
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{isImperial ? "Lbs" : "Kg"}</label>
                  <input data-testid="live-weight" type="number" step="0.5" value={draft.weight} onChange={(e) => setDraft((d) => ({ ...d, weight: e.target.value }))} className={inputCls} />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">RPE</label>
                  <input data-testid="live-rpe" type="number" step="0.5" min={1} max={10} value={draft.rpe} onChange={(e) => setDraft((d) => ({ ...d, rpe: e.target.value }))} className={inputCls} />
                </div>
                <button
                  type="button"
                  data-testid="live-log-set-btn"
                  onClick={logSet}
                  className="col-span-4 blaze-btn py-2 text-xs inline-flex items-center justify-center gap-2"
                >
                  <Check className="h-3.5 w-3.5" /> Log set
                </button>
              </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add exercise mid-session */}
      <div className="flex gap-2 mb-4">
        <input
          data-testid="live-add-exercise-input"
          value={newExName}
          onChange={(e) => setNewExName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addExercise()}
          placeholder="Add exercise (e.g. Lateral Raise)"
          className={`${inputCls} flex-1`}
        />
        <button type="button" data-testid="live-add-exercise-btn" onClick={addExercise} className="ghost-btn px-4 py-2 text-xs inline-flex items-center gap-2">
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>

      {/* Notes + finish */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <textarea
          data-testid="live-notes"
          value={session.notes}
          onChange={(e) => setSession((s) => ({ ...s, notes: e.target.value }))}
          placeholder="Session notes..."
          className={`${inputCls} flex-1`}
          rows={2}
        />
        <button
          type="button"
          data-testid="live-finish-btn"
          onClick={finish}
          disabled={totalSets === 0}
          className="blaze-btn px-6 py-3 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Flag className="h-3.5 w-3.5" /> Finish workout ({totalSets} sets)
        </button>
      </div>
    </div>
  );
}
